import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Globe, Plus, RefreshCw, CheckCircle, XCircle, AlertTriangle,
  Link, Unlink, Settings, BarChart2, Clock, Zap
} from 'lucide-react';
import { channelManagerAPI, propertyAPI } from '../../lib/api';
import toast from 'react-hot-toast';

const OTA_LOGOS = {
  airbnb: '🏠',
  booking_com: '🔵',
  vrbo: '🌴',
  expedia: '✈️',
  tripadvisor: '🦉'
};

const STATUS_BADGE = {
  active: { label: 'Connected', cls: 'badge-green' },
  inactive: { label: 'Disconnected', cls: 'badge-gray' },
  error: { label: 'Error', cls: 'badge-red' },
  pending: { label: 'Pending', cls: 'badge-yellow' }
};

const TABS = ['Channels', 'Property Mappings', 'Sync Logs'];

export default function ChannelManagerPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Channels');
  const [accounts, setAccounts] = useState([]);
  const [enabledOTAs, setEnabledOTAs] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [selectedOTA, setSelectedOTA] = useState(null);
  const [credentials, setCredentials] = useState({});
  const [mappingForm, setMappingForm] = useState({ property: '', ota: '', otaListingId: '', otaListingUrl: '', rateMarkup: 0 });
  const [connecting, setConnecting] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [acctRes, propRes] = await Promise.all([
        channelManagerAPI.getAccounts(),
        propertyAPI.getAll({ limit: 100 })
      ]);
      setAccounts(acctRes.data.data || []);
      setEnabledOTAs(acctRes.data.enabledOTAs || []);
      setProperties(propRes.data.data || []);
    } catch {
      toast.error('Failed to load channel manager data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMappings = useCallback(async () => {
    try {
      const res = await channelManagerAPI.getMappings();
      setMappings(res.data.data || []);
    } catch {
      toast.error('Failed to load mappings');
    }
  }, []);

  const fetchSyncLogs = useCallback(async () => {
    try {
      const res = await channelManagerAPI.getSyncLogs();
      setSyncLogs(res.data.data || []);
    } catch {
      toast.error('Failed to load sync logs');
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => {
    if (activeTab === 'Property Mappings') fetchMappings();
    if (activeTab === 'Sync Logs') fetchSyncLogs();
  }, [activeTab, fetchMappings, fetchSyncLogs]);

  const handleConnect = async (e) => {
    e.preventDefault();
    setConnecting(true);
    try {
      await channelManagerAPI.connectChannel({ ota: selectedOTA.ota, credentials });
      toast.success(`${selectedOTA.displayName} connected!`);
      setShowConnectModal(false);
      setCredentials({});
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Connection failed');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async (ota) => {
    if (!window.confirm(`Disconnect ${ota}?`)) return;
    try {
      await channelManagerAPI.disconnectChannel(ota);
      toast.success('Disconnected');
      fetchAll();
    } catch {
      toast.error('Failed to disconnect');
    }
  };

  const handleTestConnection = async (ota) => {
    try {
      const res = await channelManagerAPI.testConnection(ota);
      toast.success(`Connection OK: ${res.data.data?.accountName || ota}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Connection test failed');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await channelManagerAPI.triggerSync();
      toast.success(res.data.message || 'Sync complete');
      fetchAll();
      if (activeTab === 'Sync Logs') fetchSyncLogs();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateMapping = async (e) => {
    e.preventDefault();
    try {
      await channelManagerAPI.createMapping(mappingForm);
      toast.success('Property mapped successfully');
      setShowMappingModal(false);
      setMappingForm({ property: '', ota: '', otaListingId: '', otaListingUrl: '', rateMarkup: 0 });
      fetchMappings();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create mapping');
    }
  };

  const handleDeleteMapping = async (id) => {
    if (!window.confirm('Remove this mapping?')) return;
    try {
      await channelManagerAPI.deleteMapping(id);
      toast.success('Mapping removed');
      fetchMappings();
    } catch {
      toast.error('Failed to remove mapping');
    }
  };

  const handleSyncMapping = async (id) => {
    try {
      const res = await channelManagerAPI.triggerMappingSync(id);
      toast.success('Sync complete');
      fetchMappings();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Sync failed');
    }
  };

  const connectedOTAs = accounts.filter(a => a.status === 'active').map(a => a.ota);

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg" /></div>;

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Channel Manager</h1>
          <p className="page-subtitle">Connect OTAs, sync rates & availability, import bookings</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={handleSync} disabled={syncing}>
            <RefreshCw size={16} style={{ animation: syncing ? 'spin 1s linear infinite' : 'none' }} />
            {syncing ? 'Syncing...' : 'Sync All'}
          </button>
          {activeTab === 'Property Mappings' && (
            <button className="btn btn-primary" onClick={() => setShowMappingModal(true)}>
              <Plus size={16} /> Map Property
            </button>
          )}
          {activeTab === 'Channels' && (
            <button className="btn btn-primary" onClick={() => setShowConnectModal(true)}>
              <Plus size={16} /> Connect Channel
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { icon: Globe, label: 'Connected Channels', value: accounts.filter(a => a.status === 'active').length, color: '#3B82F6' },
          { icon: Link, label: 'Mapped Properties', value: mappings.length || '—', color: '#10B981' },
          { icon: Zap, label: 'Available OTAs', value: enabledOTAs.length, color: '#8B5CF6' },
          { icon: BarChart2, label: 'Total Syncs', value: accounts.reduce((s, a) => s + (a.stats?.totalSyncs || 0), 0), color: '#F59E0B' }
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="card" style={{ padding: '16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon size={20} color={color} />
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{value}</div>
                <div style={{ fontSize: 12, color: '#6B7280' }}>{label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t} className={`tab ${activeTab === t ? 'active' : ''}`} onClick={() => setActiveTab(t)}>{t}</button>
        ))}
      </div>

      {/* Channels Tab */}
      {activeTab === 'Channels' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {enabledOTAs.length === 0 && (
            <div className="card" style={{ gridColumn: '1/-1', textAlign: 'center', padding: 40 }}>
              <Globe size={40} color="#9CA3AF" style={{ margin: '0 auto 12px' }} />
              <h3 style={{ fontWeight: 600, marginBottom: 6 }}>No OTAs Available</h3>
              <p style={{ fontSize: 14, color: '#6B7280' }}>Contact your Super Admin to enable OTA channels.</p>
            </div>
          )}
          {enabledOTAs.map(ota => {
            const account = accounts.find(a => a.ota === ota.ota);
            const isConnected = account?.status === 'active';
            const badge = STATUS_BADGE[account?.status || 'inactive'];
            return (
              <div key={ota.ota} className="card">
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ fontSize: 32 }}>{OTA_LOGOS[ota.ota]}</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 16 }}>{ota.displayName}</div>
                      <span className={`badge ${badge.cls}`}>{badge.label}</span>
                    </div>
                  </div>
                  {isConnected && (
                    <CheckCircle size={20} color="#10B981" />
                  )}
                </div>

                {ota.supportedFeatures && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                    {Object.entries(ota.supportedFeatures).filter(([, v]) => v).map(([k]) => (
                      <span key={k} style={{ fontSize: 11, padding: '2px 8px', background: '#EFF6FF', color: '#1D4ED8', borderRadius: 999 }}>
                        {k.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                    ))}
                  </div>
                )}

                {account && (
                  <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 12, display: 'flex', gap: 16 }}>
                    <span>✅ {account.stats?.successfulSyncs || 0} syncs</span>
                    <span>📦 {account.stats?.bookingsImported || 0} bookings</span>
                    {account.lastSyncAt && <span><Clock size={10} /> {new Date(account.lastSyncAt).toLocaleDateString()}</span>}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8 }}>
                  {isConnected ? (
                    <>
                      <button className="btn btn-sm btn-secondary" onClick={() => handleTestConnection(ota.ota)}>Test</button>
                      <button className="btn btn-sm btn-secondary" onClick={() => handleDisconnect(ota.ota)} style={{ color: '#EF4444' }}>
                        <Unlink size={12} /> Disconnect
                      </button>
                    </>
                  ) : (
                    <button className="btn btn-sm btn-primary" onClick={() => { setSelectedOTA(ota); setShowConnectModal(true); }}>
                      <Link size={12} /> Connect
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Property Mappings Tab */}
      {activeTab === 'Property Mappings' && (
        <div className="card" style={{ padding: 0 }}>
          {mappings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>
              <Link size={40} style={{ margin: '0 auto 12px' }} />
              <h3 style={{ fontWeight: 600, color: '#374151' }}>No Property Mappings</h3>
              <p style={{ fontSize: 14 }}>Map your properties to OTA listings to start syncing.</p>
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Property</th><th>OTA</th><th>Listing ID</th><th>Sync Settings</th>
                  <th>Last Synced</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map(m => (
                  <tr key={m._id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{m.property?.title || '—'}</div>
                      <div style={{ fontSize: 12, color: '#9CA3AF' }}>{m.property?.address?.city}</div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>{OTA_LOGOS[m.ota]}</span>
                        <span style={{ textTransform: 'capitalize', fontWeight: 500 }}>{m.ota.replace('_', '.')}</span>
                      </div>
                    </td>
                    <td>
                      <code style={{ fontSize: 12, background: '#F3F4F6', padding: '2px 6px', borderRadius: 4 }}>{m.otaListingId}</code>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {m.syncRates && <span className="badge badge-blue" style={{ fontSize: 10 }}>Rates</span>}
                        {m.syncAvailability && <span className="badge badge-green" style={{ fontSize: 10 }}>Avail</span>}
                        {m.syncBookings && <span className="badge badge-yellow" style={{ fontSize: 10 }}>Bookings</span>}
                      </div>
                      {m.rateMarkup !== 0 && <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{m.rateMarkup > 0 ? '+' : ''}{m.rateMarkup}% markup</div>}
                    </td>
                    <td>
                      {m.lastRateSyncAt ? (
                        <span style={{ fontSize: 12, color: '#6B7280' }}>{new Date(m.lastRateSyncAt).toLocaleString()}</span>
                      ) : <span style={{ color: '#9CA3AF', fontSize: 12 }}>Never</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-sm btn-secondary" onClick={() => handleSyncMapping(m._id)} title="Sync now">
                          <RefreshCw size={12} />
                        </button>
                        <button className="btn btn-sm btn-secondary" onClick={() => handleDeleteMapping(m._id)} style={{ color: '#EF4444' }} title="Remove">
                          <Unlink size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Sync Logs Tab */}
      {activeTab === 'Sync Logs' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {syncLogs.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>
              <Clock size={40} style={{ margin: '0 auto 12px' }} />
              <h3 style={{ fontWeight: 600, color: '#374151' }}>No Sync Logs</h3>
              <p style={{ fontSize: 14 }}>Sync history will appear here after you trigger a sync.</p>
            </div>
          ) : syncLogs.map(log => (
            <div key={log._id} className="card" style={{ padding: '16px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 24 }}>{OTA_LOGOS[log.ota]}</span>
                  <div>
                    <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{log.ota?.replace('_', '.')}</div>
                    <div style={{ fontSize: 12, color: '#6B7280' }}>
                      Last sync: {log.lastSyncAt ? new Date(log.lastSyncAt).toLocaleString() : 'Never'}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: 700, fontSize: 18 }}>{log.stats?.totalSyncs || 0}</div>
                    <div style={{ fontSize: 11, color: '#9CA3AF' }}>Total</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: 700, fontSize: 18, color: '#10B981' }}>{log.stats?.successfulSyncs || 0}</div>
                    <div style={{ fontSize: 11, color: '#9CA3AF' }}>Success</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: 700, fontSize: 18, color: '#EF4444' }}>{log.stats?.failedSyncs || 0}</div>
                    <div style={{ fontSize: 11, color: '#9CA3AF' }}>Failed</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: 700, fontSize: 18, color: '#3B82F6' }}>{log.stats?.bookingsImported || 0}</div>
                    <div style={{ fontSize: 11, color: '#9CA3AF' }}>Imported</div>
                  </div>
                  {log.lastSyncStatus && (
                    <span className={`badge ${log.lastSyncStatus === 'success' ? 'badge-green' : 'badge-red'}`}>
                      {log.lastSyncStatus}
                    </span>
                  )}
                </div>
              </div>
              {log.lastSyncError && (
                <div style={{ marginTop: 10, padding: '8px 12px', background: '#FEF2F2', borderRadius: 6, fontSize: 12, color: '#991B1B' }}>
                  ⚠️ {log.lastSyncError}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Connect Modal */}
      {showConnectModal && (
        <div className="modal-overlay" onClick={() => { setShowConnectModal(false); setSelectedOTA(null); setCredentials({}); }}>
          <div className="modal modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">
                {selectedOTA ? `Connect ${selectedOTA.displayName}` : 'Connect a Channel'}
              </span>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => { setShowConnectModal(false); setSelectedOTA(null); setCredentials({}); }}>✕</button>
            </div>

            {!selectedOTA ? (
              <div className="modal-body">
                <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 16 }}>Select an OTA to connect:</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {enabledOTAs.filter(o => !connectedOTAs.includes(o.ota)).map(ota => (
                    <button key={ota.ota} className="btn btn-secondary"
                      style={{ justifyContent: 'flex-start', gap: 12, padding: '12px 16px' }}
                      onClick={() => setSelectedOTA(ota)}>
                      <span style={{ fontSize: 24 }}>{OTA_LOGOS[ota.ota]}</span>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontWeight: 600 }}>{ota.displayName}</div>
                      </div>
                    </button>
                  ))}
                  {enabledOTAs.filter(o => !connectedOTAs.includes(o.ota)).length === 0 && (
                    <p style={{ textAlign: 'center', color: '#9CA3AF', padding: 20 }}>All available OTAs are connected.</p>
                  )}
                </div>
              </div>
            ) : (
              <form onSubmit={handleConnect}>
                <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={{ padding: '10px 14px', background: '#EFF6FF', borderRadius: 8, fontSize: 13, color: '#1D4ED8' }}>
                    Enter your {selectedOTA.displayName} API credentials. These are encrypted and stored securely.
                  </div>
                  {selectedOTA.requiredCredentials?.map(cred => (
                    <div key={cred.key} className="form-group">
                      <label className="form-label">{cred.label} *</label>
                      <input
                        type={cred.isSecret ? 'password' : 'text'}
                        className="form-input"
                        placeholder={cred.isSecret ? '••••••••••••' : `Enter ${cred.label}`}
                        value={credentials[cred.key] || ''}
                        onChange={e => setCredentials(p => ({ ...p, [cred.key]: e.target.value }))}
                        required
                      />
                    </div>
                  ))}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setSelectedOTA(null)}>Back</button>
                  <button type="submit" className="btn btn-primary" disabled={connecting}>
                    {connecting ? 'Connecting...' : `Connect ${selectedOTA.displayName}`}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Mapping Modal */}
      {showMappingModal && (
        <div className="modal-overlay" onClick={() => setShowMappingModal(false)}>
          <div className="modal modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Map Property to OTA</span>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setShowMappingModal(false)}>✕</button>
            </div>
            <form onSubmit={handleCreateMapping}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-group">
                  <label className="form-label">Property *</label>
                  <select className="form-select" value={mappingForm.property} onChange={e => setMappingForm(p => ({ ...p, property: e.target.value }))} required>
                    <option value="">Select property...</option>
                    {properties.map(p => <option key={p._id} value={p._id}>{p.title}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">OTA Channel *</label>
                  <select className="form-select" value={mappingForm.ota} onChange={e => setMappingForm(p => ({ ...p, ota: e.target.value }))} required>
                    <option value="">Select OTA...</option>
                    {accounts.filter(a => a.status === 'active').map(a => (
                      <option key={a.ota} value={a.ota}>{OTA_LOGOS[a.ota]} {a.ota.replace('_', '.')}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">OTA Listing ID *</label>
                  <input className="form-input" placeholder="e.g. 12345678" value={mappingForm.otaListingId}
                    onChange={e => setMappingForm(p => ({ ...p, otaListingId: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Listing URL</label>
                  <input className="form-input" placeholder="https://airbnb.com/rooms/..." value={mappingForm.otaListingUrl}
                    onChange={e => setMappingForm(p => ({ ...p, otaListingUrl: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Rate Markup (%)</label>
                  <input type="number" className="form-input" placeholder="0" value={mappingForm.rateMarkup}
                    onChange={e => setMappingForm(p => ({ ...p, rateMarkup: Number(e.target.value) }))} />
                  <span style={{ fontSize: 12, color: '#6B7280' }}>Positive = markup, Negative = discount</span>
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {[['syncRates', 'Sync Rates'], ['syncAvailability', 'Sync Availability'], ['syncBookings', 'Import Bookings']].map(([key, label]) => (
                    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                      <input type="checkbox" checked={mappingForm[key] !== false}
                        onChange={e => setMappingForm(p => ({ ...p, [key]: e.target.checked }))} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowMappingModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Create Mapping</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
